/**
 * Auth Guard Hook
 * Protects routes by redirecting non-authenticated users to home with login modal
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

/**
 * Hook to guard protected routes
 * Redirects non-authenticated users to home page with login modal
 * 
 * @param {boolean} enabled - Whether to enable the guard (default: true)
 * @returns {object} Auth state { user, loading }
 * 
 * @example
 * function ProtectedPage() {
 *   const { user, loading } = useAuthGuard();
 *   
 *   if (loading) return <div>Loading...</div>;
 *   if (!user) return null; // Redirecting...
 *   
 *   return <div>Protected content</div>;
 * }
 */
export const useAuthGuard = (enabled = true) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Skip guard if disabled
    if (!enabled) return;

    // Wait for auth state to load
    if (loading) return;

    // Redirect if not authenticated
    if (!user) {
      console.log('🚫 Auth Guard: User not logged in, redirecting to home');
      
      // Navigate to home and trigger login modal
      navigate('/', { 
        replace: true,
        state: { 
          openLoginModal: true,
          message: 'Please log in to continue',
          returnUrl: window.location.pathname // Save where they tried to go
        }
      });
    }
  }, [user, loading, enabled, navigate]);

  return { user, loading };
};

/**
 * Hook to check if user has active paid plan
 * Redirects free users to pricing page
 * 
 * @param {boolean} enabled - Whether to enable the check (default: true)
 * @returns {object} Plan state { user, loading, isPaidUser, planType, userData }
 * 
 * @example
 * function PaidFeature() {
 *   const { user, loading, isPaidUser } = usePaidPlanGuard();
 *   
 *   if (loading) return <div>Loading...</div>;
 *   if (!isPaidUser) return null; // Redirecting...
 *   
 *   return <div>Paid feature content</div>;
 * }
 */
export const usePaidPlanGuard = (enabled = true) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        // Fetch user data from Firestore
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            setUserData(userSnap.data());
          } else {
            setUserData({ planType: 'free' }); // Default to free if no document
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUserData({ planType: 'free' });
        }
      } else {
        setUserData(null);
      }
      
      setLoading(false);
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Skip guard if disabled
    if (!enabled) return;

    // Wait for auth state to load
    if (loading) return;

    // Must be logged in first
    if (!user) return;

    // Check if user has userData loaded
    if (!userData) return;

    // Check if user has free plan
    const planType = userData.planType || 'free';
    const isFreeUser = planType === 'free';

    if (isFreeUser) {
      console.log('💰 Plan Guard: Free user detected, redirecting to pricing');
      
      navigate('/pricing', {
        replace: true,
        state: {
          message: 'Upgrade to download resumes and access premium features',
          returnUrl: window.location.pathname
        }
      });
    }
  }, [user, userData, loading, enabled, navigate]);

  const planType = userData?.planType || 'free';
  const isPaidUser = planType !== 'free';

  return { 
    user, 
    loading, 
    isPaidUser,
    planType,
    userData
  };
};

export default useAuthGuard;
